// Store RUNTIME de scénarios chargés depuis le BLOB (via l'API privée).
// Le contenu de jeu (scènes/chats/quiz) vient du JSON blob de l'histoire, pas
// des données bundlées. getScenarioForMission() (index.ts) consulte ce store
// en priorité, ce qui bascule TOUT le moteur (ChapterDetail, SceneReader,
// MissionCards, TaskDetail) sur le contenu du blob.

import { loadScenarioFromJsonPayload, type ScenarioChapter } from './storyLoader';

export interface RuntimeCharacter {
  id?: string;
  name: string;
  role?: string;
  color?: string;
  avatar?: string;
  avatarUrl?: string;
}

export interface RuntimeScenario {
  id: string;
  missionId: number;
  title: string;
  certification: string;
  subtitle?: string;
  chapters: ScenarioChapter[];
  characters: RuntimeCharacter[];
}

// Cover par défaut (le type EgoCrisisScenario exige un coverImage require()).
const FALLBACK_COVER = require('../../../assets/images/characters/nina_raoul_working.png');

const _store = new Map<number, RuntimeScenario>();
const _inFlight = new Set<number>();

export function getRuntimeScenario(missionId: number): RuntimeScenario | undefined {
  return _store.get(missionId);
}

/** Construit + mémorise un scénario runtime à partir du doc blob {schemaVersion, scenario}. */
export function setRuntimeScenarioFromDoc(missionId: number, doc: any): RuntimeScenario | undefined {
  const sc = doc?.scenario ?? doc;
  if (!sc || typeof sc !== 'object') return undefined;
  const loaded = loadScenarioFromJsonPayload(
    {
      id: typeof sc.id === 'string' ? sc.id : `mission-${missionId}`,
      title: typeof sc.title === 'string' ? sc.title : 'Histoire',
      subtitle: typeof sc.subtitle === 'string' ? sc.subtitle : '',
      certification: typeof sc.certification === 'string' ? sc.certification : '',
      coverImage: FALLBACK_COVER,
    },
    doc,
  );
  const characters: RuntimeCharacter[] = Array.isArray(sc.characters)
    ? sc.characters.filter((c: any) => c && typeof c.name === 'string')
    : [];
  const runtime: RuntimeScenario = {
    id: loaded.id,
    missionId,
    title: loaded.title,
    certification: loaded.certification,
    subtitle: loaded.subtitle,
    chapters: loaded.chapters,
    characters,
  };
  _store.set(missionId, runtime);
  return runtime;
}

/** Résout un personnage du scénario blob par nom (exact, prénom, ou id). */
export function getRuntimeCharacter(missionId: number, nameOrId: string): RuntimeCharacter | undefined {
  const sc = _store.get(missionId);
  if (!sc || !nameOrId) return undefined;
  const q = nameOrId.trim().toLowerCase();
  const chars = sc.characters;
  return (
    chars.find(c => c.name.toLowerCase() === q || (c.id ?? '').toLowerCase() === q)
    ?? chars.find(c => c.name.toLowerCase().split(' ')[0] === q.split(' ')[0])
  );
}

/**
 * Charge le scénario du blob pour la mission via `fetcher` (typiquement
 * apiFetch('/me/stories/{id}/scenario')). `force` re-fetch même si déjà en cache.
 * Best-effort : en cas d'échec on garde le repli local (SCENARIO_MAP).
 */
export async function loadRuntimeScenario(
  missionId: number,
  fetcher: () => Promise<any>,
  force = false,
): Promise<void> {
  if (_inFlight.has(missionId)) return;
  if (_store.has(missionId) && !force) return;
  _inFlight.add(missionId);
  try {
    const res = await fetcher();
    const doc = res?.doc ?? res;
    if (doc) setRuntimeScenarioFromDoc(missionId, doc);
  } catch {
    /* garde le repli local */
  } finally {
    _inFlight.delete(missionId);
  }
}
